import { authedFetch, UnauthorizedError } from '@apis/auth';

/**
 * Analytics participants — FE mirrors of the BFF response shapes for the
 * analytics surface (Daily Route, Incident, etc.). The BFF resolves the
 * display name (e.g. `name || manager.name || ''`) and sorts alphabetically
 * server-side, so consumers don't re-sort.
 */

export interface AnalyticsTag {
  tagId: string;
  tagNick: string;
  /** Server-resolved display name (`name || manager.name || ''`). */
  name: string;
  profession: string | null;
}

export interface AnalyticsEquipment {
  equipmentId: string;
  name: string;
  type: string;
  typeNick: string;
}

async function handle(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  // 401 reaches us only after authedFetch's refresh-and-retry has already
  // failed and dispatched `dorca:auth-expired`; we just surface the error
  // type so React Query lands in an error state until the redirect fires.
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`request failed: ${res.status} ${body?.error?.message ?? ''}`);
}

export async function fetchSiteTags(
  siteId: string,
  signal?: AbortSignal,
): Promise<AnalyticsTag[]> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/tags`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as AnalyticsTag[];
}

export async function fetchSiteEquipment(
  siteId: string,
  signal?: AbortSignal,
): Promise<AnalyticsEquipment[]> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/equipment`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as AnalyticsEquipment[];
}
